import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import lodashTemplate from 'lodash/template';
import classnames from 'classnames';
import AppPropTypes from 'proptypes/AppPropTypes';

import Alert from '@glu/alerts-react';
import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';
import { withStyles } from '@glu/theming';

import { ALERTVARIATIONS, DEFAULT_PER_PAGE } from 'constants.js';
import { manageImportMaps } from 'api/wireImports/service';

// components
import SpinResolver from 'components/spinResolver/SpinResolver';
import BasicGrid from 'components/basicGrid/BasicGrid';
import useLocale from 'components/useLocale';
import ModalNotification from 'components/modalNotification/ModalNotification';

import styles from './manageImportMaps.styles';
import { columns, handleImportMapActions } from './manageImportMapsColumns';
import { HeaderContent } from './partials';

const { SUCCESS, WARNING } = ALERTVARIATIONS;

const cleanup = (str) =>
  str
    .replace(/(%[a-zA-Z0-9_]+)/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+$/g, '');
const template = (str, o) => cleanup(str.replace(/%([a-zA-Z0-9_]+)/g, ($0, $1) => o[$1]));

const AlertMessage = ({ data = {}, message }) => {
  AlertMessage.propTypes = {
    data: PropTypes.shape({}).isRequired,
    message: PropTypes.string.isRequired
  };

  const map = data.mapName;

  if (!map) {
    return cleanup(message);
  }

  const regex = /(.+?)(%[a-zA-Z_]+)(.+)/g;
  const match = regex.exec(message);
  const [, prepend, name, rest] = match;

  return (
    <>
      {prepend}
      <strong>{template(name, { map })}</strong>
      {rest}
    </>
  );
};

function ManageImportMapsComponent({ classes, history, location }) {
  const baseURL = '/payments/managePaymentMap';
  const locale = useLocale();
  const [messages, setMessages] = useState(get(location, 'state.messages', []));
  const [services, setServices] = useState(null);
  const [initial, setInitial] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState(null);

  useEffect(() => {
    setInitial(true);
  }, []);

  const partialProps = {
    classes,
    locale,
    onAddMap: () =>
      history.push({ pathname: `${baseURL}/create`, state: { previous: location.pathname } })
  };

  // in case we have some messages, reload the grid
  if (messages.length > 0 && initial && services) {
    const isCreateOrModify = get(messages, '0.modifyOrCreate', false);

    if (!isCreateOrModify) {
      services.reload({});
    }
  }

  const closeDeleteModalFunc = useCallback(() => {
    setDeleteModalData(null);
    setShowDeleteModal(false);
  }, [setShowDeleteModal, setDeleteModalData]);

  const showDeleteModalFunc = useCallback(
    (deleteModalData) => {
      setDeleteModalData(deleteModalData);
      setShowDeleteModal(true);
    },
    [setShowDeleteModal, setDeleteModalData]
  );

  const onDeleteMap = useCallback(() => {
    const reloadGrid = deleteModalData?.reload;

    handleImportMapActions('delete', deleteModalData);
    closeDeleteModalFunc();

    return reloadGrid({});
  }, [deleteModalData, closeDeleteModalFunc]);

  const gridColumns = (reload) =>
    columns({
      history,
      location,
      locale,
      baseURL,
      updates: {
        setMessages
      },
      showDeleteModalFunc,
      reload
    });
  return (
    <SpinResolver services={[manageImportMaps]}>
      {({ reloads: [sync], results: [{ data }], reload, lastSync }) => {
        if (!services) {
          setServices({ reload });
        }
        return (
          <>
            <ModalNotification
              variant="error"
              show={showDeleteModal}
              onHide={closeDeleteModalFunc}
              suppressIcon={false}
              data-qa="delete-ach-map-modal"
            >
              <div className="modalWrapper">
                <div
                  className={classnames('deleteWarning', {
                    [classes.modalHeader]: deleteModalData?.data?.type === 'ACH'
                  })}
                >
                  {deleteModalData?.data?.type === 'ACH'
                    ? locale['payments.manageImportMaps.delete.warning'].toUpperCase()
                    : locale['payments.manageImportMaps.delete.warning']}
                </div>
                <div className={deleteModalData?.data?.type === 'ACH' ? classes.modalBody : ''}>
                  <div className="deleteContent">
                    {lodashTemplate(locale['payments.manageImportMaps.delete.content'])({
                      mapName: deleteModalData?.data?.mapName
                    })}
                  </div>
                  <div className="deleteConfirm">
                    {deleteModalData?.data?.type === 'ACH'
                      ? locale['wireImports.deleteMapDoYouWishToProceed']
                      : locale['payments.manageImportMaps.delete.confirm']}
                  </div>
                </div>
                {deleteModalData?.data?.type !== 'ACH' && <br />}
                <Button
                  dataQa="wire-import-delete"
                  className="btn btn-primary"
                  variant={PRIMARY}
                  onClick={onDeleteMap}
                >
                  {deleteModalData?.data?.type === 'ACH'
                    ? locale['common.continue']
                    : locale['common.delete']}
                </Button>

                <Button
                  className="btn btn-tertiary"
                  variant={TERTIARY}
                  onClick={closeDeleteModalFunc}
                >
                  {deleteModalData?.data?.type === 'ACH'
                    ? locale['common.cancel']
                    : locale['common.close']}
                </Button>
              </div>
            </ModalNotification>

            <section>
              {messages.length > 0 &&
                messages.map((message) => (
                  <>
                    <Alert
                      dataQa="manage-import-map-alert"
                      key={`alertMessage-${message.title}`}
                      message={<AlertMessage data={message.data} message={message.title} />}
                      variant={message.success ? SUCCESS : WARNING}
                      onHide={() => setMessages([])}
                      details={Boolean(message.details)}
                    >
                      {message.details && (
                        <AlertMessage data={message.data} message={message.title} />
                      )}
                    </Alert>
                  </>
                ))}
              <BasicGrid
                gridId="manage-import-maps-grid"
                showSavedViews
                columnDefs={gridColumns(reload)}
                records={data}
                // eslint-disable-next-line react/jsx-props-no-spreading
                headerLeftContent={<HeaderContent {...partialProps} />}
                defaultState={{
                  pageSize: DEFAULT_PER_PAGE
                }}
                defaultColDef={{
                  filterable: true,
                  sortable: true
                }}
                sync={sync}
                lastSync={lastSync}
                label={locale['common.manageImportMaps']}
              />
            </section>
          </>
        );
      }}
    </SpinResolver>
  );
}

ManageImportMapsComponent.propTypes = {
  history: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({}).isRequired,
  classes: AppPropTypes.jssClasses.isRequired
};

const Styled = withStyles(styles)(ManageImportMapsComponent);
const Routed = withRouter(Styled);
export default Routed;
